'use client'

import { useEffect, useRef, type ReactNode } from 'react'

type Direction = 'up' | 'down' | 'left' | 'right' | 'none' | 'scale' | 'scale-up'

interface ScrollRevealProps {
  children: ReactNode
  direction?: Direction
  delay?: number
  className?: string
  threshold?: number
}

export function ScrollReveal({
  children,
  direction = 'up',
  delay = 0,
  className = '',
  threshold = 0.15,
}: ScrollRevealProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const mobile = window.innerWidth < 768
    const effectiveDelay = mobile ? Math.min(delay * 0.5, 150) : delay
    if (el.style.transitionDelay !== `${effectiveDelay}ms`) {
      el.style.transitionDelay = `${effectiveDelay}ms`
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.classList.add('revealed')
          observer.unobserve(el)
        }
      },
      {
        threshold: mobile ? Math.min(threshold, 0.05) : threshold,
        rootMargin: mobile ? '0px 0px -20px 0px' : '0px 0px -40px 0px',
      }
    )

    observer.observe(el)
    return () => observer.disconnect()
  }, [threshold, delay])

  const directionClass = {
    up: 'reveal-up',
    down: 'reveal-down',
    left: 'reveal-left',
    right: 'reveal-right',
    none: 'reveal-fade',
    scale: 'reveal-scale',
    'scale-up': 'reveal-scale-up',
  }[direction]

  return (
    <div
      ref={ref}
      className={`reveal ${directionClass} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  )
}
